import { CommonModule, NgClass } from '@angular/common';
import type { AfterViewInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { LocaleEnum } from '@wizbii/utils/models';
import { loadScript } from '@wizbii/utils/tracking';

@Component({
  imports: [CommonModule, NgClass],
  selector: 'app-trust-box-widget',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- eslint-disable @angular-eslint/template/no-call-expression -->

    @switch (trustBoxType()) {
      @case ('slider') {
        <section class="tw:mb-[1.875rem]">
          <div
            class="trustpilot-widget tw:pt-5 tw:bg-white tw:rounded-[20px]"
            [id]="trustBoxId()"
            [attr.data-locale]="dataLocale()"
            data-template-id="54ad5defc6454f065c28af8b"
            data-businessunit-id="6057b7fc5c783500013272ea"
            data-style-height="240px"
            data-style-width="100%"
            data-theme="light"
            data-tags="TopAvis"
            data-stars="1,2,3,4,5"
          >
            <a [href]="trustpilotLink()" target="_blank" rel="noopener">Trustpilot</a>
          </div>
        </section>
      }
      @case ('microTrustScore') {
        <div [ngClass]="{ 'tw:pointer-events-none tw:cursor-default': readonly() }">
          <div
            class="trustpilot-widget"
            [id]="trustBoxId()"
            [attr.data-locale]="dataLocale()"
            data-template-id="5419b637fa0340045cd0c936"
            data-businessunit-id="6057b7fc5c783500013272ea"
            data-style-height="20px"
            data-style-width="100%"
            data-theme="light"
            data-font-family="Montserrat"
            data-text-color="#000028"
          >
            <a [href]="trustpilotLink()" target="_blank" rel="noopener">Trustpilot</a>
          </div>
        </div>
      }
    }
  `,
})
export class TrustBoxWidgetComponent implements AfterViewInit {
  readonly trustBoxType = input<'microTrustScore' | 'slider' | null>('slider');
  readonly trustBoxId = input.required<string>();
  readonly readonly = input<boolean>(false);
  readonly trustBoxLocale = input<LocaleEnum>(LocaleEnum.fr_FR);

  readonly mapDataLocale: Record<LocaleEnum, string> = {
    [LocaleEnum.fr_FR]: 'fr-FR',
    [LocaleEnum.it_IT]: 'it-IT',
    [LocaleEnum.es_ES]: 'es-ES',
    [LocaleEnum.en_GB]: 'en-GB',
    [LocaleEnum.de_DE]: 'de-DE',
  };

  readonly dataLocale = computed(() => this.mapDataLocale[this.trustBoxLocale()]);
  readonly lang = computed(() => this.trustBoxLocale()?.split('_')?.[0] ?? 'fr');
  readonly trustpilotLink = computed(() => `https://${this.lang()}.trustpilot.com/review/money.wizbii.com`);

  constructor() {
    if (!(window as any).Trustpilot) {
      loadScript('//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js').catch(() => {
        throw new Error('Unable to load TrustBox script');
      });
    }
  }

  ngAfterViewInit() {
    const loadTrustpilot = setInterval(() => {
      if (!(window as any).Trustpilot) {
        return;
      }
      const trustBoxRef = document.getElementById(this.trustBoxId());
      (window as any).Trustpilot?.loadFromElement(trustBoxRef);
      clearInterval(loadTrustpilot);
    }, 0);
  }
}
